import { useEffect, useRef, type CSSProperties } from "react";
import { formatClock } from "@heroncendre/player";
import type { Guide } from "./useGuide.js";
import { useChaines } from "./useChaines.js";
import { usePiegeFocus } from "./usePiegeFocus.js";
import { chapitresVus, compteurVus, statutChapitre, type Statut } from "./statuts.js";
import { CREME, COULEURS, POLICE_UI_SPLASH, SAUGE, carre, contour, ellipse, surtitre, titreItalien, verre } from "./guideStyles.js";
import { IconeBoussole, Losange } from "./icones.js";
import "./guide.css";

/**
 * ============================================================================
 *  ChapterMenu.tsx — Choisir un chapitre du guide, à tout moment
 * ----------------------------------------------------------------------------
 *  Référence : `sources/design/design_handoff_menu_chapitres/`. Un panneau centré sous
 *  la bande haute sur ordinateur, une feuille basse sur téléphone ; trois déclencheurs
 *  l'ouvrent (le titre de la bande haute, « Chapitres » en mode main, « Tous les
 *  chapitres » en fin de chapitre).
 *
 *  Ce composant ne décide de rien : la pause et la reprise de la voix, le changement de
 *  chapitre sont dans `menuChapitres.ts` (testé), les statuts dans `statuts.ts` — une
 *  seule règle pour les puces et le compteur de l'en-tête.
 *
 *  Modal : `aria-modal`, focus piégé puis rendu à son bouton, Échap et clic sur le voile
 *  ferment. Le focus d'ouverture va sur le chapitre courant, ou à défaut sur « À suivre ».
 * ==========================================================================*/

/** Sous la bande haute (91 px), à 8 px d'elle. */
const TOP_GUIDE = 99;
const TOP_MAIN = 76;

export function ChapterMenu({ guide, etroit }: { guide: Guide; etroit: boolean }) {
  const { t, scenes, parcours, language } = useChaines();
  const racine = useRef<HTMLDivElement>(null);
  const { menu, chapitres, indexChapitre, progression, actions } = guide;
  const ouvert = menu.ouvert;

  const ids = chapitres.map((c) => c.id);
  const vus = progression.watched;
  const statut = (index: number): Statut => statutChapitre(index, ids, vus, indexChapitre);
  const nVus = compteurVus(ids, vus);
  const finDeChapitre = guide.mode === "fin" || guide.mode === "exploration";

  // Échap ferme — avant tout autre raccourci du guide (en mode main, Échap « reprend le guide »).
  useEffect(() => {
    if (!ouvert) return;
    const surTouche = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopImmediatePropagation();
      actions.fermerMenu();
    };
    window.addEventListener("keydown", surTouche, true);
    return () => window.removeEventListener("keydown", surTouche, true);
  }, [ouvert, actions]);

  usePiegeFocus(
    racine,
    ouvert,
    () =>
      racine.current?.querySelector<HTMLElement>('[aria-current="step"]') ??
      racine.current?.querySelector<HTMLElement>('[data-a-suivre="true"]'),
    () => guide.declencheur.current,
  );

  if (!ouvert) return null;

  const top = guide.mode === "main" || guide.mode === "exploration" ? TOP_MAIN : TOP_GUIDE;

  return (
    <>
      <div
        aria-hidden
        className="gd-voile"
        onClick={actions.fermerMenu}
        style={{ position: "absolute", inset: 0, background: "rgba(11,19,16,.55)", pointerEvents: "auto", zIndex: 25 }}
      />
      <div
        ref={racine}
        role="dialog"
        aria-modal="true"
        aria-label={t("chapters")}
        lang={language}
        className={etroit ? "gd-menu-feuille" : "gd-menu-panneau"}
        style={{
          ...panneau,
          pointerEvents: "auto",
          zIndex: 26,
          ...(etroit
            ? { left: 0, right: 0, bottom: 0, maxHeight: "86%", borderRadius: "2px 2px 0 0", paddingBottom: "calc(14px + env(safe-area-inset-bottom))" }
            : { top, left: "50%", width: "min(540px, calc(100% - 24px))", maxHeight: "calc(100% - 110px)" }),
        }}
      >
        {etroit && (
          <div aria-hidden style={{ display: "flex", justifyContent: "center", paddingBottom: 6 }}>
            <span style={{ width: 44, height: 3, borderRadius: 2, background: "rgba(246,238,227,.35)" }} />
          </div>
        )}

        <header style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
            <span style={surtitre}>
              <Losange couleur={SAUGE} />
              {t("guided", { parcours: parcours(guide.route) })}
            </span>
            <h2 style={{ ...titreItalien(30), margin: 0 }}>{t("chapters")}</h2>
            <span style={{ fontSize: 12.5, color: "#E3D9C8" }}>
              {t(nVus === 1 ? "watched_one" : "watched_other", { n: nVus, total: chapitres.length })}
            </span>
          </div>
          <button type="button" aria-label={t("close")} style={carre(44)} onClick={actions.fermerMenu}>
            <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" aria-hidden>
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </header>

        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6, overflowY: "auto", minHeight: 0 }}>
          {chapitres.map((chapitre) => {
            const courant = chapitre.index === indexChapitre;
            const s = statut(chapitre.index);
            const vu = chapitresVus(ids, vus).includes(chapitre.id);
            // Au bout du chapitre la barre est PLEINE. La position rapportée par le lecteur s'arrête un
            // souffle avant la durée de la piste : lue telle quelle, la barre n'irait pas jusqu'au bout.
            const avancement = !courant
              ? 0
              : finDeChapitre
                ? 1
                : chapitre.duree > 0
                  ? Math.max(0, Math.min(1, guide.temps / chapitre.duree))
                  : 0;
            return (
              <li key={chapitre.id}>
                <button
                  type="button"
                  className="gd-ligne"
                  aria-current={courant ? "step" : undefined}
                  data-a-suivre={s === "a-suivre" ? "true" : undefined}
                  onClick={() => actions.choisirChapitre(chapitre.index)}
                  style={{
                    ...ligne,
                    background: courant ? "rgba(246,238,227,.1)" : vu ? "rgba(246,238,227,.03)" : "transparent",
                    borderColor: courant ? "rgba(246,238,227,.55)" : "rgba(246,238,227,.12)",
                  }}
                >
                  <span aria-hidden style={pastille(s, vu)}>
                    {chapitre.index + 1}
                  </span>
                  <span style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
                    <span style={{ ...titreItalien(20), ...ellipse }}>{chapitre.titre}</span>
                    {/* Le statut n'a pas de colonne : il est à droite de la ligne des scènes, juste
                        AU-DESSUS de la barre, qui court ainsi jusqu'au bord de la ligne. */}
                    <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, minHeight: 20 }}>
                      <span style={{ fontSize: 11.5, color: "#CFC4B2" }}>
                        {scenes(chapitre.segments.length)} · {formatClock(chapitre.duree)}
                      </span>
                      <Statut statut={s} t={t} />
                    </span>
                    {courant && (
                      <span aria-hidden style={{ display: "block", height: 2, background: "rgba(246,238,227,.18)" }}>
                        <span style={{ display: "block", height: "100%", width: `${avancement * 100}%`, background: SAUGE }} />
                      </span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        <footer style={{ display: "flex", justifyContent: "center", paddingTop: 4 }}>
          <button type="button" style={{ ...contour, border: "none", fontSize: 12, letterSpacing: ".12em", textTransform: "uppercase" }} onClick={() => actions.quitter("libre")}>
            <IconeBoussole taille={16} />
            {t("backFree")}
          </button>
        </footer>
      </div>
    </>
  );
}

/** « ✓ VU » en sauge, « EN COURS » en crème, puce crème « À SUIVRE » ; rien pour un chapitre à venir. */
function Statut({ statut, t }: { statut: Statut; t: ReturnType<typeof useChaines>["t"] }) {
  if (statut === "") return <span aria-hidden />;
  const base: CSSProperties = { fontSize: 11, fontWeight: 600, letterSpacing: ".1em", textTransform: "uppercase", whiteSpace: "nowrap" };
  if (statut === "vu") return <span style={{ ...base, color: SAUGE }}>✓ {t("seen")}</span>;
  if (statut === "en-cours") return <span style={{ ...base, color: CREME }}>{t("current")}</span>;
  return <span style={{ ...base, color: COULEURS.vert, background: CREME, padding: "3px 8px", borderRadius: 2 }}>{t("upNext")}</span>;
}

const panneau: CSSProperties = {
  ...verre,
  background: "rgba(16,26,23,.86)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  boxShadow: "0 16px 40px rgba(8,14,12,.45)",
  position: "absolute",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: 14,
  padding: 18,
};

const ligne: CSSProperties = {
  appearance: "none",
  width: "100%",
  minHeight: 68,
  display: "grid",
  gridTemplateColumns: "40px 1fr",
  alignItems: "center",
  gap: 14,
  padding: "10px 14px 10px 12px",
  textAlign: "left",
  color: CREME,
  fontFamily: POLICE_UI_SPLASH,
  border: "1px solid",
  borderRadius: 2,
  cursor: "pointer",
};

/** Pastille carrée de 40 px : contour sauge (vu), crème pleine (à suivre), contour discret sinon. */
const pastille = (statut: Statut, vu: boolean): CSSProperties => ({
  width: 40,
  height: 40,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
  fontFamily: "var(--police-splash-titre)",
  fontSize: 19,
  borderRadius: 2,
  ...(statut === "a-suivre"
    ? { background: CREME, color: COULEURS.vert, border: "1px solid " + CREME }
    : vu
      ? { border: "1px solid rgba(185,205,180,.55)", color: SAUGE }
      : { border: "1px solid rgba(246,238,227,.3)", color: CREME }),
});
